import React, { useState, useEffect } from 'react';
import { FileText, Mail, Phone, User, Building2, Calendar, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { useFlashMessage } from '../components/FlashMessage';

const DemoApplications = () => {
  const { isSystemAdmin } = useAuth();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminAPI.getApplications();
      if (response.data.success) {
        setApplications(response.data.data.applications);
      }
    } catch (err) {
      console.error('Failed to load applications:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (application) => {
    setApplicationToDelete(application);
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const response = await adminAPI.deleteApplication(applicationToDelete.id);
      if (response.data.success) {
        showSuccess(`"${applicationToDelete.name}" başvurusu başarıyla silindi.`);
        setDialogOpen(false);
        setApplicationToDelete(null);
        loadApplications();
      } else {
        showDanger(response.data.message || 'Başvuru silinirken hata oluştu');
      }
    } catch (err) {
      console.error('Failed to delete application:', err);
      showDanger(`Başvuru silinirken hata oluştu: ${handleApiError(err)}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isSystemAdmin()) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-red-700">Bu sayfaya erişim yetkiniz yok.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Demo Başvuruları</h1>
        <p className="text-gray-600">Web sitesinden gelen demo talepleri</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Başvurular ({applications.length})
          </h3>
        </div>
        <div className="card-body">
          {applications.length > 0 ? (
            <div className="space-y-4">
              {applications.map((app) => (
                <div key={app.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-900">{app.name}</span>
                        {app.kvkk_accepted && (
                          <CheckCircle className="w-4 h-4 text-green-500" title="KVKK Kabul Edildi" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">{app.company}</span>
                      </div>
                      {app.email && (
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">{app.email}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{app.phone}</span>
                      </div>
                      {app.subject && (
                        <div className="mb-2">
                          <span className="text-sm font-medium text-gray-700">Konu: </span>
                          <span className="text-sm text-gray-600">{app.subject}</span>
                        </div>
                      )}
                      {app.message && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{app.message}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {new Date(app.created_at).toLocaleString('tr-TR')}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleDeleteClick(app)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Sil
                    </button>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Henüz başvuru bulunmuyor</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Başvuruyu Sil"
        message={`${applicationToDelete?.name} başvurusunu silmek istediğinizden emin misiniz?`}
        confirmText="Sil"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />

      <FlashContainer />
    </div>
  );
};

export default DemoApplications;
